import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Calendar, Phone, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getConfiguracion, buscarMisCitas } from "@/lib/api";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const DIAS_ORDEN = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function MisCitasBuscador() {
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [citas, setCitas] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState("");

  const handleBuscar = async (e) => {
    e.preventDefault();
    if (!nombre.trim() || !telefono.trim()) return;
    setBuscando(true);
    setError("");
    setCitas(null);
    try {
      const res = await buscarMisCitas(nombre.trim(), telefono.trim());
      setCitas(res.data.citas);
    } catch (err) {
      setError("Error al buscar. Verifica tus datos.");
    } finally {
      setBuscando(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-lg font-heading mb-1 flex items-center gap-2">
          <Search className="h-5 w-5" /> Consultar mi cita
        </h2>
        <p className="text-sm text-muted-foreground mb-4">Introduce tu nombre y telefono para ver tus proximas citas</p>
        <form onSubmit={handleBuscar} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Tu nombre" value={nombre} onChange={e => setNombre(e.target.value)} className="h-11" data-testid="buscar-nombre" />
            <Input placeholder="Tu telefono" type="tel" value={telefono} onChange={e => setTelefono(e.target.value)} className="h-11" data-testid="buscar-telefono" />
          </div>
          <Button type="submit" variant="outline" className="w-full h-11" disabled={!nombre.trim() || !telefono.trim() || buscando} data-testid="btn-buscar-cita">
            {buscando ? "Buscando..." : "Buscar mi cita"}
          </Button>
        </form>
        {error && <p className="text-sm text-destructive mt-3">{error}</p>}
        {citas !== null && citas.length === 0 && (
          <p className="text-sm text-muted-foreground mt-3 text-center">No tienes citas pendientes</p>
        )}
        {citas && citas.length > 0 && (
          <div className="mt-4 space-y-2">
            {citas.map(cita => (
              <div key={cita.id} className="p-3 rounded-lg border bg-accent/5 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{cita.servicio_nombre}</span>
                  <Badge variant="secondary">{cita.hora_inicio}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {format(parseISO(cita.fecha), "EEEE d 'de' MMMM", { locale: es })} con {cita.peluquero_nombre}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function HomePage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await getConfiguracion();
      setConfig(res.data);
    } catch (error) {
      console.error("Error loading config:", error);
    } finally {
      setLoading(false);
    }
  };

  const getHorarioFormateado = () => {
    if (!config?.horario_general || config.horario_general.length === 0) {
      return null;
    }
    
    // Reordenar para mostrar Lunes primero
    const horarios = [];
    for (let i = 0; i < 7; i++) {
      const diaIndex = i; // 0=Lunes en nuestro sistema
      const horario = config.horario_general.find(h => h.dia === diaIndex);
      if (horario) {
        horarios.push({
          ...horario,
          nombreDia: DIAS_ORDEN[i]
        });
      }
    }
    return horarios;
  };

  const getGoogleMapsUrl = () => {
    if (!config?.direccion) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(config.direccion)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  const horarios = getHorarioFormateado();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading mb-4" data-testid="home-title">
            {config?.nombre_negocio || "Barbería Clásica"}
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            Tu peluquería de confianza
          </p>
          <Button
            asChild
            size="lg"
            className="h-16 px-12 text-xl bg-accent hover:bg-accent/90 text-white rounded-full shadow-lg"
            data-testid="btn-reservar"
          >
            <Link to="/reservar">
              <Calendar className="h-6 w-6 mr-3" />
              Reservar cita
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="h-12 px-8 text-base rounded-full"
            data-testid="btn-cancelar-home"
          >
            <Link to="/cancelar">
              Cancelar cita
            </Link>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Información */}
          <div className="space-y-6">
            {/* Dirección */}
            {config?.direccion && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-heading mb-2">Dirección</h2>
                      <p className="text-muted-foreground mb-3">{config.direccion}</p>
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="gap-2"
                      >
                        <a href={getGoogleMapsUrl()} target="_blank" rel="noopener noreferrer">
                          <MapPin className="h-4 w-4" />
                          Ver en Google Maps
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Horario */}
            {horarios && horarios.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-heading mb-3">Horario</h2>
                      <div className="space-y-2">
                        {horarios.map((h, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="font-medium">{h.nombreDia}</span>
                            <span className={h.activo ? "text-foreground" : "text-muted-foreground"}>
                              {h.activo ? (
                                h.inicio2 
                                  ? `${h.inicio} - ${h.fin} / ${h.inicio2} - ${h.fin2}`
                                  : `${h.inicio} - ${h.fin}`
                              ) : "Cerrado"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contacto por teléfono */}
          </div>

          {/* Mapa */}
          {config?.direccion && (
            <div>
              <a
                href={getGoogleMapsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full"
              >
                <Card className="h-full min-h-[400px] flex flex-col items-center justify-center cursor-pointer hover:shadow-lg transition-shadow group">
                  <CardContent className="flex flex-col items-center justify-center text-center p-8 gap-4">
                    <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <MapPin className="h-8 w-8 text-accent" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg mb-1">Cómo llegar</p>
                      <p className="text-muted-foreground text-sm">{config.direccion}</p>
                    </div>
                    <span className="text-sm font-medium text-accent flex items-center gap-1 group-hover:underline">
                      Abrir en Google Maps <ChevronRight className="h-4 w-4" />
                    </span>
                  </CardContent>
                </Card>
              </a>
            </div>
          )}
        </div>

        {/* Buscar mi cita */}
        <div className="mt-12">
          <MisCitasBuscador />
        </div>

        {/* CTA Final */}
        <div className="mt-12 text-center">
          <Card className="bg-accent/5 border-accent/20">
            <CardContent className="p-8">
              <h2 className="text-2xl font-heading mb-4">¿Listo para tu próximo corte?</h2>
              <p className="text-muted-foreground mb-6">
                Reserva tu cita en menos de un minuto
              </p>
              <Button
                asChild
                size="lg"
                className="h-14 px-10 text-lg bg-accent hover:bg-accent/90"
              >
                <Link to="/reservar">
                  Reservar ahora
                  <ChevronRight className="h-5 w-5 ml-2" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg font-heading mb-2">{config?.nombre_negocio || "Barbería Clásica"}</p>
          {config?.direccion && (
            <p className="text-sm opacity-70">{config.direccion}</p>
          )}
          <div className="mt-4 pt-4 border-t border-white/20 space-y-3">
            <div className="flex justify-center gap-4 text-xs opacity-70">
              <Link to="/privacidad" className="hover:opacity-100 underline">Politica de Privacidad</Link>
              <Link to="/aviso-legal" className="hover:opacity-100 underline">Aviso Legal</Link>
              <Link to="/condiciones" className="hover:opacity-100 underline">Condiciones de Uso</Link>
            </div>
            <Link 
              to="/admin/login" 
              className="text-xs opacity-50 hover:opacity-100 transition-opacity block"
            >
              Panel de administración
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
